export class ArrUtil {
  static groupBy<T, K extends keyof T>(
    items: T[],
    selector: (item: T) => T[K]
  ): Map<T[K], T[]> {
    return items.reduce((acc: Map<T[K], T[]>, value: T) => {
      const key = selector(value)
      acc.set(key, [...(acc.get(key) || []), value])
      return acc
    }, new Map())
  }

  static xor<T>(arr1: T[], arr2: T[]): T[] {
    if (!arr1 || !arr2) {
      if (!arr1 && !arr2) {
        return []
      }
      return !arr1 ? arr2 : arr1
    }
    const s1 = new Set(arr1)
    const s2 = new Set(arr2)
    return [
      ...[...s1].filter(v => !s2.has(v)),
      ...[...s2].filter(v => !s1.has(v))
    ]
  }

  static partition<T>(
    list: T[] | ReadonlyArray<T>,
    predicate: (item: T) => boolean
  ): [T[], T[]] {
    return (list as T[]).reduce(
      (acc: [T[], T[]], item: T) => {
        acc[predicate(item) ? 0 : 1].push(item)

        return acc
      },
      [[], []]
    )
  }

  static createIndex<T, U>(arr: T[], makeIx: (a: T) => U): Map<U, T> {
    if (!arr) {
      return new Map()
    }
    const res: Map<U, T> = new Map()
    arr.forEach(a => res.set(makeIx(a), a))
    return res
  }

  static insert<T>(array: ReadonlyArray<T>, index: number, ...items: T[]): T[] {
    return [
      ...array.slice(0, index),
      ...items,
      ...array.slice(index)
    ] as unknown as T[]
  }

  static move<T>(array: T[], fromIndex: number, toIndex: number): T[] {
    if (fromIndex === toIndex) {
      return array
    }
    const item = array[fromIndex]
    return ArrUtil.insert(
      ArrUtil.deletedAtIndex(array, fromIndex),
      toIndex,
      item
    )
  }

  static swap<T>(
    array: ReadonlyArray<T>,
    index: number,
    item: T
  ): ReadonlyArray<T> {
    return ArrUtil.insert(array, index, item).filter(
      (_: T, i: number) => i !== index + 1
    ) as unknown as ReadonlyArray<T>
  }

  static last<T>(array?: ArrayLike<T>): T | undefined {
    return array && array[array.length - 1]
  }

  static intersection<T>(x: T[], y: T[]): T[] {
    return x.filter(n => y.includes(n))
  }

  static areListsEqual<T>(x: T[], y: T[]): boolean {
    return (
      x.length === y.length && x.length === ArrUtil.intersection(x, y).length
    )
  }

  static areListsNotEqual<T>(x: T[], y: T[]): boolean {
    return !ArrUtil.areListsEqual(x, y)
  }

  static shallowEqual<T>(x: T[], y: T[]): boolean {
    return (
      x.length === y.length && x.every((xItem, xIndex) => xItem === y[xIndex])
    )
  }

  static flatten<T>(list: any[]): T[] {
    return list.reduce(
      (a: T[], b: unknown) =>
        a.concat(Array.isArray(b) ? ArrUtil.flatten(b as unknown[]) : (b as T)),
      []
    )
  }

  static unique<T, I>(list: T[], index?: (t: T) => I): T[]
  static unique<T, I>(list: T[], index: (t: T) => I): T[] {
    if (typeof index === 'undefined') {
      index = (a: any) => a
    }

    if (!list) {
      return []
    }
    const ids: Set<I> = new Set()
    const res: T[] = []
    list.forEach(t => {
      const ix = index(t)
      if (!ids.has(ix)) {
        ids.add(ix)
        res.push(t)
      }
    })
    return res
  }

  /**
   * This is needed because iterable is not
   * implemented on FileList (there was a bug, should be checked later)
   */
  static forEach<T>(obj: ArrayLike<T>, func: (x: T, ix: number) => void): void {
    for (let i = 0, len = obj.length; i < len; i++) {
      func(obj[i], i)
    }
  }

  static changedAtIndex<T>(arr: T[], el: T, ix: number): T[] {
    if (ix === -1) {
      return arr
    }
    return [...arr.slice(0, ix), el, ...arr.slice(ix + 1)]
  }

  static union<T>(arr1: T[] | undefined, arr2: T[] | undefined): T[] {
    return [...new Set([...(arr1 ?? []), ...(arr2 ?? [])])]
  }

  static xorBy<T>(
    arr: T[],
    items: T[],
    selector: (value: T) => string | number | boolean
  ): T[] {
    const arrKeys = new Set(arr.map(selector))
    const itemsKeys = new Set(items.map(selector))
    return arr
      .filter(x => !itemsKeys.has(selector(x)))
      .concat(items.filter(x => !arrKeys.has(selector(x))))
  }

  static defined<T>(arr: (T | undefined)[]): T[] {
    return arr.filter(el => typeof el !== 'undefined') as unknown as T[]
  }

  static filterMap<T, R>(arr: T[], selector: (item: T) => R | null): R[] {
    const newArr: R[] = []
    arr.forEach(x => {
      const v = selector(x)
      if (v !== null) {
        newArr.push(v)
      }
    })
    return newArr
  }

  static deletedAtIndex<T>(arr: T[], ix: number): T[] {
    if (ix === -1) {
      return arr
    }
    return [...arr.slice(0, ix), ...arr.slice(ix + 1)]
  }

  static diff<T>(oldArr: T[], newArr: T[]): {add: T[]; remove: T[]} {
    const oldSet = new Set(oldArr)
    const newSet = new Set(newArr)
    return {
      add: newArr.filter(x => !oldSet.has(x)),
      remove: oldArr.filter(x => !newSet.has(x))
    }
  }

  static delete<T>(arr: T[], item: T): T[] {
    return arr.filter(x => x !== item)
  }

  static batch<T>(xs: T[], n: number): T[][] {
    const res: T[][] = []
    let i = 0
    do {
      res.push(xs.slice(i, i + n))
      i += n
    } while (i < xs.length)
    return res
  }

  static filterUndefined<T>(arr: (T | undefined)[]): T[] {
    return arr.reduce((acc, curr) => {
      if (curr !== undefined) {
        acc.push(curr)
      }
      return acc
    }, [] as T[])
  }

  static filterNil<T>(arr: (T | undefined)[]): T[] {
    return arr.reduce((acc, curr) => {
      if (curr) {
        acc.push(curr)
      }
      return acc
    }, [] as T[])
  }
}
